import { useEffect, useRef, useCallback } from "react";
import { resolveLocation } from "./geolocation";
import { enqueue } from "./offlineQueue";

const CONNECTION_CACHE_KEY = "nafe-last-connection";

function getConnection() {
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
}

function getStoredConnection() {
  try {
    const saved = JSON.parse(localStorage.getItem(CONNECTION_CACHE_KEY) || "null");
    return saved && Number.isFinite(saved.api_latency_ms) ? saved : null;
  } catch {
    return null;
  }
}

function saveConnection(metrics) {
  try {
    localStorage.setItem(CONNECTION_CACHE_KEY, JSON.stringify(metrics));
  } catch {
    // A leitura de conexão deve continuar funcionando mesmo se o storage estiver indisponível.
  }
}

function connectionQuality({ api_latency_ms: apiLatency, rtt, downlink, effectiveType, online }) {
  if (!online) return "offline";
  if (effectiveType === "slow-2g" || effectiveType === "2g" || (Number.isFinite(apiLatency) && apiLatency > 1000) || (Number.isFinite(rtt) && rtt > 1000) || (Number.isFinite(downlink) && downlink < 0.5)) return "poor";
  if (effectiveType === "3g" || (Number.isFinite(apiLatency) && apiLatency > 500) || (Number.isFinite(rtt) && rtt > 500) || (Number.isFinite(downlink) && downlink < 1.5)) return "unstable";
  if ((Number.isFinite(apiLatency) && apiLatency <= 300) || (Number.isFinite(rtt) && rtt <= 200 && Number.isFinite(downlink) && downlink >= 3)) return "good";
  return "unknown";
}

/**
 * Dados de conexão disponíveis no browser. A latência é a da última chamada
 * bem-sucedida à nossa API, que é mais útil que apenas navigator.onLine.
 */
function connectionMetrics() {
  const connection = getConnection();
  const previous = getStoredConnection();
  const metrics = {
    network_type: connection?.type || "unknown",
    network_effective_type: connection?.effectiveType || null,
    network_rtt: Number.isFinite(connection?.rtt) ? connection.rtt : null,
    network_downlink: Number.isFinite(connection?.downlink) ? connection.downlink : null,
    api_latency_ms: previous?.api_latency_ms ?? null,
    connection_quality: "unknown",
  };
  metrics.connection_quality = connectionQuality({ ...metrics, online: navigator.onLine });
  return metrics;
}

/** Poll API on interval for near-real-time dashboards (NA-1-508). */
export function useLivePoll(fetchFn, intervalMs = 15000, enabled = true) {
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  const refresh = useCallback(() => {
    if (enabled) fnRef.current?.();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [refresh, intervalMs, enabled]);

  return refresh;
}

export function useConnectionSync(onOnline) {
  useEffect(() => {
    const handler = () => {
      if (navigator.onLine) onOnline?.();
    };
    window.addEventListener("online", handler);
    return () => window.removeEventListener("online", handler);
  }, [onOnline]);
}

async function postOrEnqueuePing(api, body) {
  const payload = { ...body, ...connectionMetrics() };
  if (navigator.onLine) {
    const startedAt = Date.now();
    try {
      await api.post("/tracking/ping", payload);
      const api_latency_ms = Date.now() - startedAt;
      const connection = getConnection();
      const measured = {
        api_latency_ms,
        network_rtt: Number.isFinite(connection?.rtt) ? connection.rtt : null,
        network_downlink: Number.isFinite(connection?.downlink) ? connection.downlink : null,
        network_effective_type: connection?.effectiveType || null,
      };
      saveConnection(measured);
      return;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) return;
    }
  }
  await enqueue({ url: "/tracking/ping", method: "POST", body: payload });
}

export function useGpsTracking(api, enabled, intervalMs = 30000) {
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const send = async () => {
      try {
        const loc = await resolveLocation({ allowIp: true, allowCache: true });
        if (cancelled || !loc) return;
        await postOrEnqueuePing(api, {
          lat: loc.lat,
          lng: loc.lng,
          accuracy: loc.accuracy,
        });
      } catch {
        
      }
    };

    void send();
    const id = setInterval(() => { void send(); }, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [api, enabled, intervalMs]);
}

export async function reportTrackingPing(api, loc) {
  if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) return;
  await postOrEnqueuePing(api, {
    lat: loc.lat,
    lng: loc.lng,
    accuracy: loc.accuracy,
  });
}
